import { AddContextualData } from '@opendoor/observability/slim';
import faviconPath from '@opendoor/shared-fe/build/faviconPath';
import { getCookieParser } from 'next/dist/server/api-utils';
import Head from 'next/head';

import {
  fetchLandingPageV2BySlug,
  getCashOfferModal,
  ILandingPageV2LayoutFields,
  ILandingPageV2LayoutProps,
  LandingPageV2Props,
} from 'cms';
import { IIdToLoaderData } from 'cms/loaders';

import { DEPLOY_ENV } from 'components/globals';
import { Locale, LOCALES } from 'components/helpers/types';
import Layout from 'components/landing-pages-v2/Layout';
import { PictureSourceSpec } from 'components/shared/performance/pictures';
import { usePromptGoogleOneTap } from 'components/shared/usePromptGoogleOneTap';

import { IOpenGraphFieldsFields, ISeoBaseFieldsFields } from 'declarations/contentful';
import { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';

import { useLocale } from 'helpers/useLocale';

import { HOMEPAGE_SCHEMA } from 'schema';

const getSeoBaseTags = (fields?: ISeoBaseFieldsFields) => {
  const title = fields?.title;
  const metaDescription = fields?.metaDescription;
  const canonicalUrl = fields?.canonicalUrl;

  const robots: Array<string> = [];
  // only set these when they are explicitly set to false
  // otherwise assume indexing and follow should be allowed
  if (fields?.index === false) {
    robots.push('noindex');
  }
  if (fields?.follow === false) {
    robots.push('nofollow');
  }

  return (
    <>
      <title key="title">{title ?? "Opendoor | Sell your home the minute you're ready."}</title>
      <link rel="shortcut icon" href={faviconPath({ railsEnv: DEPLOY_ENV })} type="image/png" />
      {metaDescription ? (
        <meta key="description" name="description" content={metaDescription} />
      ) : null}
      {canonicalUrl ? <link key="canonical" rel="canonical" href={canonicalUrl} /> : null}
      {robots.length > 0 ? <meta key="robots" name="robots" content={robots.join(',')} /> : null}
    </>
  );
};

const getOpenGraphTags = (openGraphFields: IOpenGraphFieldsFields) => {
  const { url, type, title, description, image, siteName } = openGraphFields;

  return (
    <>
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:type" property="og:type" content={type} />
      <meta key="og:url" property="og:url" content={url} />
      <meta key="og:image" property="og:image" content={image} />,
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:site_name" property="og:site_name" content={siteName} />
    </>
  );
};

// when a no-code landing page needs to preload an image for performance reasons,
// it can include a `preloadImages` field in the loader data
// this function crunches the loader data and pulls out all the preload images
// so that the top-level page can include those preload urls in the Head section
export const getPreloadsFromLoaderData = (idToLoaderData: IIdToLoaderData) => {
  return Object.values(idToLoaderData)
    .map((loaderData: any) => {
      return loaderData?.preloadImages;
    })
    .filter((preload: any) => preload)
    .flat();
};

export const getLandingpageV2ServerSideProps: CacheSafeGetServerSideProps<
  LandingPageV2Props
> = async (context) => {
  if (!context.params?.slug) {
    return {
      notFound: true,
    };
  }

  let pageNum = 1;
  if (context.query.page) {
    pageNum = parseInt(context.query.page as string);
  }
  if (pageNum < 1) pageNum = 1;

  // collapse the slug from an array to a single string
  let slug = '';
  // if slug is a string and not an array
  if (typeof context.query.slug === 'string') {
    slug = context.query.slug;
  } else if (Array.isArray(context.query.slug)) {
    slug = context.query.slug.join('/');
  }

  const cookieParser = getCookieParser(context.req.headers);
  const localeFromCookie = cookieParser().locale;

  const locale: Locale = localeFromCookie === LOCALES.SPANISH ? LOCALES.SPANISH : LOCALES.ENGLISH;
  try {
    const props = await fetchLandingPageV2BySlug({
      slug,
      requestUrl: context.resolvedUrl,
      pageNum,
      locale,
    });

    if (!props) {
      return {
        notFound: true,
      };
    }
    // check if the page is set to public, and reject if appropriate
    // this is intended to block non-public pages from being directly accessed by users,
    // while still letting them be used in experiments as treatments
    if (context.query.public && props.public === false) {
      return {
        notFound: true,
      };
    }
    let showModal = false;
    const excludedSlugs = [
      'builder',
      'agent',
      'real-estate',
      'homepage',
      'login',
      'register',
      'reset-password',
      'calculator',
      'careers',
      'safety',
      'privacy',
    ];

    let modalProps;
    if (excludedSlugs.every((excluded) => !slug.includes(excluded))) {
      showModal = true;
      modalProps = await getCashOfferModal();
    }
    return {
      props: {
        ...props,
        modalFields:
          showModal && modalProps
            ? {
                title: modalProps.fields.title,
                description: modalProps.fields.description,
                analyticsId: modalProps.fields.analyticsId,
              }
            : null,
        showModal: !!(showModal && modalProps),
      },
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return {
      notFound: true,
    };
  }
};

const LandingPageV2Layout = <T extends ILandingPageV2LayoutFields>(
  props: ILandingPageV2LayoutProps<T>,
) => {
  usePromptGoogleOneTap(`landing-pages-v2-${props.fields.slug}`, undefined, props.hideGoogleOneTap);

  useLocale();

  const contextualData = { landingPageSlug: props.fields.slug };

  const imagePreloads = getPreloadsFromLoaderData(props.idToLoaderData);

  const openGraphFields = props.fields.openGraph?.fields;

  const appleStatusBarColor =
    props.fields.slug === 'homepage-syw-v2-home-guide' ? 'black' : 'default';

  const shouldIncludeTrustpilot = props.fields?.shouldShowTrustpilotReviews;

  return (
    <AddContextualData data={contextualData}>
      <Head>
        {getSeoBaseTags(props.fields.seoBase?.fields)}
        {openGraphFields && getOpenGraphTags(openGraphFields)}
        <meta name="apple-mobile-web-app-status-bar-style" content={appleStatusBarColor} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(HOMEPAGE_SCHEMA) }}
        />
        {shouldIncludeTrustpilot && (
          <script
            type="text/javascript"
            src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            async
          />
        )}
        {(imagePreloads as PictureSourceSpec).map((h) => (
          <link
            key={h.key}
            rel="preload"
            as="image"
            media={h.media}
            // @ts-expect-error: react incorrectly capitalizes this if you use
            // the "typescript" suggestion of `imgSrcSet`
            imagesrcset={h.srcSet}
            fetchpriority="high"
          />
        ))}
      </Head>
      {/* slash is to match against nav links for detecting current page */}
      <Layout
        header={props.fields?.header}
        slug={`/${props.fields?.slug}`}
        footer={props.fields?.footer}
        showModal={props.showModal}
        modalTitle={props.modalFields?.title}
        modalDescription={props.modalFields?.description}
        modalAnalyticsId={props.modalFields?.analyticsId}
        extraLegalText={props.fields?.extraLegalText}
        showEligibilityLegal={props.fields?.showEligibilityLegal}
        showCustomerTestimonialLegal={props.fields?.showCustomerTestimonialLegal}
        showThePdpCopyrightLegal={props.fields?.showThePdpCopyrightLegal}
        showPromotionLegal={props.fields?.showPromotionLegal}
        showLanguageToggle={props.fields?.showLanguageToggle}
      >
        {props.children}
      </Layout>
    </AddContextualData>
  );
};

export default LandingPageV2Layout;
